import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import FormGenerator from '@sharedCR/common/formGenerator';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';

import { openDialogAction } from 'store/global/actions';

import { pagesPropTypes } from '../../../MainContent.constants';
import { DeclarationType } from '../Declarations.constants';

import DeclarationsCounselingForm from './DeclarationsCounselingForm/DeclarationsCounselingForm';
import { getConfig } from './HomeownershipEducationAndHousingCounseling.config';
import { agencyPlacesWidgetMap } from './HomeownershipEducationAndHousingCounseling.constants';
import { normalizeForm } from './HomeownershipEducationAndHousingCounseling.utils';

const HomeownershipEducationAndHousingCounseling = ({
  changedFields,
  isLoading,
  isSaving,
  form,
  validationErrors,
  validateField,
  handleChange,
  entityId,
  setEntityConfig,
  setForm,
  setInForm,
  setIsTouched,
  disabledPendingChangesHighlight,
  pendingChanges,
}) => {
  const dispatch = useDispatch();

  const sectionForm =
    form[entityId]?.declarations?.homeownershipEducationAndHousingCounseling ?? {};

  const {
    isBorrowerCompletedHomeownershipEducation,
    isBorrowerCompletedHousingCounseling,
    homeownershipEducationHousingCounselingAgencyName,
    housingCounselingAgencyName,
  } = sectionForm;

  const isHomeownershipEducationNameOfTheHousingEducationProgram =
    !!homeownershipEducationHousingCounselingAgencyName;
  const isHousingCounselingNameOfTheHousingEducationProgram = !!housingCounselingAgencyName;

  const customSetInForm = useCallback(
    (declarationType, dataToSetInForm, customAction) => {
      const sectionFormRef = { current: null };

      setForm(prev => {
        sectionFormRef.current = prev[entityId]?.declarations?.[declarationType];

        if (!sectionFormRef.current) {
          return prev;
        }

        return {
          ...prev,
          [entityId]: {
            ...prev[entityId],
            declarations: {
              ...prev[entityId]?.declarations,
              [declarationType]: {
                ...prev[entityId]?.declarations[declarationType],
                ...dataToSetInForm,
              },
            },
          },
        };
      });

      setIsTouched(true);

      customAction?.(sectionFormRef.current);
    },
    [entityId, setForm, setIsTouched],
  );

  const onAddCounselingAgency = useCallback(
    agencyType => {
      dispatch(
        openDialogAction({
          isRestyled: true,
          header: 'Add Counseling Agency',
          size: ModalSize.LARGE,
          body: dialogProps => (
            <DeclarationsCounselingForm
              agencyType={agencyType}
              entityId={entityId}
              setInParentForm={customSetInForm}
              validateField={validateField}
              {...dialogProps}
            />
          ),
          disableEnforceFocus: true,
        }),
      );
    },
    [customSetInForm, validateField, entityId, dispatch],
  );

  const onRemoveCounselingAgency = useCallback(
    agencyType => {
      const dataToSetInForm = agencyPlacesWidgetMap.reduce((acc, { fieldName }) => {
        acc[`${agencyType}${fieldName}`] = null;

        return acc;
      }, {});

      customSetInForm(
        DeclarationType.HOMEOWNERSHIP_EDUCATION_AND_HOUSING_COUNSELING,
        dataToSetInForm,
      );
    },
    [customSetInForm],
  );

  const config = useMemo(
    () =>
      getConfig({
        entityId,
        isBorrowerCompletedHomeownershipEducation,
        isBorrowerCompletedHousingCounseling,
        isHomeownershipEducationNameOfTheHousingEducationProgram:
          !!isHomeownershipEducationNameOfTheHousingEducationProgram,
        isHousingCounselingNameOfTheHousingEducationProgram:
          !!isHousingCounselingNameOfTheHousingEducationProgram,
        onRemoveCounselingAgency,
        onAddCounselingAgency,
      }),
    [
      entityId,
      isBorrowerCompletedHomeownershipEducation,
      isBorrowerCompletedHousingCounseling,
      isHomeownershipEducationNameOfTheHousingEducationProgram,
      isHousingCounselingNameOfTheHousingEducationProgram,
      onRemoveCounselingAgency,
      onAddCounselingAgency,
    ],
  );

  useEffect(() => {
    setForm(form => normalizeForm({ form, entityId }));
  }, [config, entityId, setForm]);

  useEffect(() => {
    if (!config) {
      return;
    }

    setEntityConfig(config);
  }, [config, setEntityConfig]);

  return (
    <FormGenerator
      isNestedForm
      changedFields={changedFields}
      config={config}
      disabledPendingChangesHighlight={disabledPendingChangesHighlight}
      form={form}
      formName={FormNames.DECLARATIONS}
      handleChange={handleChange}
      isLoading={isSaving || isLoading}
      numberOfColumns={1}
      pendingChanges={pendingChanges}
      setInForm={setInForm}
      validateField={validateField}
      validationErrors={validationErrors}
    />
  );
};

HomeownershipEducationAndHousingCounseling.propTypes = pagesPropTypes;

export default memo(HomeownershipEducationAndHousingCounseling);
